<template>
  <section class="project-section">
    <header class="section-header">
      <h3 class="section-title">{{ $t('Projects.New project popup.Select_goal_collection_title') }}</h3>
      <p class="section-description">
        {{ $t('Projects.New project popup.Select_goal_collection_description') }}
      </p>
    </header>

    <div v-if="goalCollections.length === 0" class="empty-state">
      <svg class="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <p class="text-gray-500">{{ $t('Collections.No collections available') }}</p>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
      <div
        v-for="collection in goalCollections"
        :key="collection.id"
        class="goal-card group"
        :class="{ 'goal-card--selected': selectedCollection === collection.id }"
        @click="selectCollection(collection.id)"
        :data-cy="'goal-collection-card-' + collection.id"
      >
        <!-- Selection indicator -->
        <div class="absolute top-3 right-3 z-10">
          <div
            class="selection-indicator"
            :class="{ 'selection-indicator--active': selectedCollection === collection.id }"
          >
            <svg
              v-if="selectedCollection === collection.id"
              class="w-4 h-4 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>

        <!-- Chart container - prominent display -->
        <div class="goal-card-chart">
          <RoseChartD3
            :chartId="`goal-collection-${collection.id}`"
            :categories="getGoalCollectionCategoriesHandler(collection)"
            :datasets="getGoalCollectionDatasetHandler(collection)"
            :options="{
              canSelectCategory: false,
              canSaveRatings: false,
              clickable: false,
              canHover: false,
              showIcons: false,
              maxValue: collection.maxValue || 100,
              steps: collection.steps || 5,
              fullHeight: false,
              chartWidth: 200,
              chartHeight: 200,
            }"
          />
        </div>

        <!-- Card info -->
        <div class="goal-card-info">
          <div class="goal-card-header">
            <h4 class="goal-card-title">{{ collection.title }}</h4>
            <span class="goal-card-badge">
              {{ getFrameworkName(collection.frameworkId) }}
            </span>
          </div>

          <p v-if="collection.description" class="goal-card-description">
            {{ collection.description }}
          </p>

          <div class="goal-card-meta">
            <div class="goal-card-stat">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{{ getGoalCount(collection) }} {{ $t('Shared.goals') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <p v-if="validationErrors?.goalCollectionId" class="validation-error">
      {{ validationErrors.goalCollectionId }}
    </p>
  </section>
</template>

<script>
import RoseChartD3 from '@/components/shared/RoseChartD3.vue'
import { getGoalCollectionCategories } from '@/composables/utils'
import { getGoalCollectionDatasets } from '@/composables/goalCollections/getGoalCollectionDatasets'

export default {
  name: 'ProjectSectionGoalCollection',
  components: {
    RoseChartD3,
  },
  props: {
    goalCollections: {
      type: Array,
      default: () => [],
    },
    selectedCollection: {
      type: [Number, String],
      default: null,
    },
    validationErrors: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      localGoalCollections: [],
    }
  },
  watch: {
    goalCollections: {
      handler (newVal) {
        console.log('!! goalCollections watcher', newVal)
        this.localGoalCollections = newVal
      },
      immediate: true,
      deep: true,
    },
  },
  emits: ['selectedCollectionUpdated'],
  computed: {
    frameworks () {
      return this.$store.getters['frameworks/list']
    },
  },
  methods: {
    getGoalCollectionCategoriesHandler (collection) {
      return getGoalCollectionCategories(collection)
    },
    getGoalCollectionDatasetHandler (collection) {
      return getGoalCollectionDatasets(collection)
    },
    selectCollection (collectionId) {
      this.$emit('selectedCollectionUpdated', collectionId)
    },
    getFrameworkName (frameworkId) {
      const framework = this.$store.getters['frameworks/list'].find((f) => f.id === frameworkId)
      return framework ? framework.title : 'Framework'
    },
    getGoalCount (collection) {
      // Count goals from goal-collections-sub-categories if available
      const subCategories = this.$store.getters['goal-collections-sub-categories/list']
        .filter((sc) => sc.goalCollectionId === collection.id)
      if (subCategories.length > 0) {
        return subCategories.length
      }
      return collection.goals?.length || 0
    },
  },
  mounted () {
    console.log('!! goalCollections mounted', this.goalCollections)
  },
}
</script>

<style scoped>
.project-section {
  @apply py-2;
}

.section-header {
  @apply mb-2;
}

.section-title {
  @apply text-xl font-semibold text-gray-800 mb-2;
}

.section-description {
  @apply text-gray-500 leading-relaxed;
}

.empty-state {
  @apply flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200;
}

.goal-card {
  @apply relative bg-white rounded-xl border-2 border-gray-200 p-5 cursor-pointer;
  @apply transition-all duration-200 ease-out;
  @apply hover:border-gray-300 hover:shadow-lg hover:-translate-y-0.5;
}

.goal-card--selected {
  @apply border-green-500 shadow-md;
  background-color: rgba(16, 185, 129, 0.05);
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}

.goal-card--selected:hover {
  @apply border-green-500;
}

.selection-indicator {
  @apply w-6 h-6 rounded-full border-2 border-gray-300 bg-white;
  @apply flex items-center justify-center;
  @apply transition-all duration-200;
}

.selection-indicator--active {
  @apply border-green-500 bg-green-500;
}

.goal-card-chart {
  @apply flex items-center justify-center py-2 mb-4;
  @apply transition-transform duration-200;
}

.goal-card:hover .goal-card-chart {
  @apply scale-105;
}

.goal-card-info {
  @apply text-center pt-4 border-t border-gray-100;
}

.goal-card-header {
  @apply flex flex-col items-center gap-2 mb-3;
}

.goal-card-title {
  @apply text-lg font-semibold text-gray-800;
}

.goal-card-badge {
  @apply text-xs font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded-full;
}

.goal-card-description {
  @apply text-sm text-gray-500 mb-4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.goal-card-meta {
  @apply flex items-center justify-center gap-4;
}

.goal-card-stat {
  @apply flex items-center gap-2 text-sm text-gray-500;
}

.goal-card-stat svg {
  @apply text-gray-400;
}

.validation-error {
  @apply text-sm text-red-500 mt-4 flex items-center gap-2;
}

.validation-error::before {
  content: '';
  @apply w-1.5 h-1.5 rounded-full bg-red-500;
}
</style>
